"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Autoplay,
  EffectCoverflow,
  EffectFade,
  Navigation,
  Pagination,
  Thumbs,
} from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css/thumbs";
import "@/styles/globals.css";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/button";
import { iconList } from "@/assets/icons";
import { Slider } from "../atoms/slider";

interface IGalleryProps extends React.ComponentPropsWithoutRef<typeof Slider> {
  children: React.ReactNode;
  containerClassName?: string;
  paginationClassName?: string;
  navigationClassName?: string;
  activeDotClassName?: string;
  inactiveDotClassName?: string;
  swiperSlideClassName?: string;
  pageNumberClassName?: string;
  loop?: boolean;
  isOnlyDots?: boolean;
  isOnlyPageNumber?: boolean;
  dotClassName?: string;
  isShowDot?: boolean;
  navigationButtonsPosition?: "sides" | "bottom";
  customNavigationButtonsStyle?: boolean;
  gridView?: boolean;
  onSwiper?: (swiper: SwiperType) => void;
  /** When true, renders a scrollable thumbnail strip below the main slider */
  showThumbnails?: boolean;
  thumbnailClassName?: string;
}
/**
 * Gallery Component
 *
 * A reusable and customizable Swiper-based image/video gallery component.
 * Supports multiple layouts, navigation styles, autoplay, pagination dots,
 * and grid-based view for displaying grouped slides.
 */
const Gallery: React.FC<IGalleryProps> = ({
  children,
  containerClassName = "w-full max-w-7xl",
  paginationClassName = "",
  activeDotClassName = "",
  swiperSlideClassName = "",
  inactiveDotClassName = "",
  pageNumberClassName = "",
  dotClassName = "",
  navigationClassName = "",
  loop = true,
  isOnlyDots = false,
  isOnlyPageNumber = false,
  isShowDot = true,
  navigationButtonsPosition = "bottom",
  customNavigationButtonsStyle = false,
  gridView = false,
  onSwiper,
  showThumbnails = false,
  thumbnailClassName = "",
  ...props
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const { AngleLeftSmall, AngleRightSmall, SliderNext, SliderPrev } = iconList;

  // Delay autoplay until browser is idle to prevent Lighthouse from flagging
  // the first image as "defer offscreen" due to carousel rotation during measurement
  useEffect(() => {
    const enableAutoplay = () => {
      setAutoplayEnabled(true);
      // Explicitly start autoplay since Swiper was initialized with autoplay: false
      swiperRef.current?.autoplay?.start();
    };

    const startWhenIdle = () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => {
          setTimeout(enableAutoplay, 4000);
        }, { timeout: 5000 });
      } else {
        setTimeout(enableAutoplay, 4000);
      }
    };

    if (document.readyState === "complete") {
      startWhenIdle();
    } else {
      window.addEventListener("load", startWhenIdle);
      return () => window.removeEventListener("load", startWhenIdle);
    }
  }, []);
  const slides = React.Children.toArray(children);

  /**
   * Navigate the main swiper to the slide at `index` when a thumbnail is clicked.
   */
  const handleThumbnailClick = (index: number) => {
    if (swiperRef.current) {
      if (loop) {
        swiperRef.current.slideToLoop(index);
      } else {
        swiperRef.current.slideTo(index);
      }
    }
  };

  /**
   * Initialize swiper reference and trigger external onSwiper callback if provided.
   */
  const handleSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    if (onSwiper) {
      onSwiper(swiper);
    }
  };

  return (
    <>
      <div
        className={cn("relative w-full overflow-x-hidden", containerClassName)}
      >
        {!isOnlyDots &&
          navigationButtonsPosition === "sides" &&
          slides?.length > 1 && (
            <>
              <Button
                hierarchy={"ghost"}
                onClick={() => swiperRef.current?.slidePrev()}
                className={cn(
                  "absolute top-1/2 left-4 z-10 h-12 w-12 -translate-y-1/2 cursor-pointer rounded-full bg-[var(--bg-btn-close)] p-2.5 text-white shadow-md backdrop-blur-sm transition hover:bg-[var(--fill-base-main)]",
                  navigationClassName,
                )}
                aria-label="Previous slide"
              >
                <AngleLeftSmall />
              </Button>
              <Button
                hierarchy={"ghost"}
                onClick={() => swiperRef.current?.slideNext()}
                aria-label="Next slide"
                className={cn(
                  "absolute top-1/2 right-4 z-10 h-12 w-12 -translate-y-1/2 cursor-pointer rounded-full bg-[var(--bg-btn-close)] p-2.5 text-white shadow-md backdrop-blur-sm transition hover:bg-[var(--fill-base-main)]",
                  navigationClassName,
                )}
              >
                <AngleRightSmall />
              </Button>
            </>
          )}

        <Slider
          spaceBetween={0}
          slidesPerView={1}
          modules={[
            Autoplay,
            Pagination,
            Navigation,
            EffectCoverflow,
            EffectFade,
            Thumbs,
          ]}
          loop={loop}
          onSwiper={handleSwiper}
          onSlideChange={(swiper) => {
            const index = Number(swiper?.realIndex);
            setActiveIndex(Number.isFinite(index) ? index : 0);
          }}
          autoplay={
            autoplayEnabled
              ? { delay: 3000, disableOnInteraction: false }
              : false
          }
          thumbs={
            showThumbnails && thumbsSwiper
              ? { swiper: thumbsSwiper }
              : undefined
          }
          {...props}
        >
          {/* Grid view mode: groups 4 slides per page */}
          {gridView
            ? Array.from({
                length: Math.ceil((slides?.length ?? 0) / 4) || 0,
              }).map((_, slideIndex) => {
                const gridViewKey = `grid-view-slide-${slideIndex}`;
                return (
                  <SwiperSlide
                    className={cn(swiperSlideClassName)}
                    key={gridViewKey}
                  >
                    <div className="grid w-full grid-cols-2 gap-4">
                      {slides
                        .slice(slideIndex * 4, slideIndex * 4 + 4)
                        .map((child, brandIndex) => {
                          const brandKey = `brand-slide-${brandIndex}`;
                          return (
                            <div
                              key={brandKey}
                              className="flex flex-col items-center"
                            >
                              <div className="flex w-full items-center overflow-hidden rounded-lg bg-white">
                                {child}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </SwiperSlide>
                );
              })
            : slides.map((child, index) => {
                const nonGridViewKey = `non-grid-view-slide-${index}`;
                return (
                  <SwiperSlide
                    className={cn(swiperSlideClassName)}
                    key={nonGridViewKey}
                  >
                    {child}
                  </SwiperSlide>
                );
              })}
        </Slider>

        {/* Pagination controls and navigation buttons (bottom) */}
        {slides?.length > 1 && (
          <div
            className={cn(
              "xs:block xs: absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 transform items-center gap-4 rounded-lg text-white",
              paginationClassName,
            )}
          >
            {!isOnlyDots &&
              navigationButtonsPosition === "bottom" &&
              !customNavigationButtonsStyle && (
                <Button
                  hierarchy={"ghost"}
                  onClick={() => swiperRef.current?.slidePrev()}
                  aria-label="Previous slide"
                  className={cn(
                    "cursor-pointer p-1 text-white transition",
                    navigationClassName,
                  )}
                >
                  <AngleLeftSmall />
                </Button>
              )}
            {/* Dots */}
            {isShowDot && (
              <div
                className={`flex items-center gap-2 ${slides?.length > 1 ? "" : "hidden"}`}
              >
                {gridView
                  ? Array.from({
                      length: Math.ceil((slides?.length ?? 0) / 4) || 0,
                    }).map((_, index) => {
                      const dotGridViewKey = `dot-grid-view-slide-${index}`;
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            loop
                              ? swiperRef.current?.slideToLoop(index)
                              : swiperRef.current?.slideTo(index)
                          }
                          aria-label={`Go to slide ${index + 1}`}
                          key={dotGridViewKey}
                          className={cn(
                            "h-2 w-2 cursor-pointer rounded-full transition-all",
                            activeIndex === index
                              ? ["bg-white", "h-3 w-3", activeDotClassName]
                              : [
                                  "bg-gray-400",
                                  "h-[9px] w-[9px]",
                                  inactiveDotClassName,
                                ],
                            dotClassName,
                          )}
                        />
                      );
                    })
                  : slides.map((_, index) => {
                      const nonDotGridViewKey = `non-dot-grid-view-${index}`;
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            loop
                              ? swiperRef.current?.slideToLoop(index)
                              : swiperRef.current?.slideTo(index)
                          }
                          key={nonDotGridViewKey}
                          aria-label={`Go to slide ${index + 1}`}
                          className={cn(
                            "h-2 w-2 cursor-pointer rounded-full transition-all",
                            activeIndex === index
                              ? ["bg-white", "h-3 w-3", activeDotClassName]
                              : [
                                  "bg-gray-400",
                                  "h-[9px] w-[9px]",
                                  inactiveDotClassName,
                                ],
                            dotClassName,
                          )}
                        />
                      );
                    })}
              </div>
            )}

            {/* Page Number */}
            {!isOnlyPageNumber && (
              <span
                className={cn(
                  "text-xs font-semibold lg:leading-3 lg:text-[var(--text-xs)]",
                  pageNumberClassName,
                )}
              >
                {activeIndex + 1}/
                {gridView
                  ? Math.ceil((slides?.length ?? 0) / 4)
                  : slides?.length}
              </span>
            )}

            {!isOnlyDots &&
              navigationButtonsPosition === "bottom" &&
              !customNavigationButtonsStyle && (
                <Button
                  hierarchy={"ghost"}
                  onClick={() => swiperRef.current?.slideNext()}
                  aria-label="Next slide"
                  className={cn(
                    "cursor-pointer p-1 text-white transition",
                    navigationClassName,
                  )}
                >
                  <AngleRightSmall aria-hidden="true" />
                </Button>
              )}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {showThumbnails && slides.length > 1 && (
        <div className={cn("mt-3", thumbnailClassName)}>
          <Swiper
            onSwiper={setThumbsSwiper}
            spaceBetween={8}
            watchSlidesProgress
            slidesPerView="auto"
            modules={[Thumbs]}
            className="thumbnail-swiper [&_.swiper-wrapper]:flex [&_.swiper-wrapper]:justify-center md:[&_.swiper-wrapper]:justify-start"
          >
            {slides.map((child, index) => {
              const thumbKey = `thumb-slide-${index}`;
              return (
                <SwiperSlide
                  key={thumbKey}
                  className="!w-[60px] cursor-pointer sm:!w-[68px] md:!w-[78px]"
                >
                  <div
                    onClick={() => handleThumbnailClick(index)}
                    className={cn(
                      "relative h-[56px] w-full overflow-hidden rounded-[4px] border transition-all duration-200 [&_img]:h-full [&>*]:max-h-full max-h-full",
                      activeIndex === index
                        ? "border-gray-400 opacity-100 shadow-sm"
                        : "border-gray-300 opacity-60 hover:opacity-90",
                    )}
                  >
                    {child}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}

      {customNavigationButtonsStyle && slides?.length > 1 && (
        <div className="xs:block flex transform items-center justify-center gap-4 rounded-lg py-10 text-white">
          <Button
            onClick={() => swiperRef.current?.slidePrev()}
            hierarchy={"outline"}
            aria-label="Previous slide"
            className="custom-prev !flex cursor-pointer rounded-full border-0 bg-transparent !px-1 !py-0 transition hover:bg-transparent active:bg-transparent"
          >
            <SliderPrev />{" "}
            <span className="text-primary-950 font-[var(--font-medium)]">
              前へ
            </span>
          </Button>
          <span className="text-xs font-semibold text-[var(--text-base-primary)] lg:text-sm">
            {activeIndex + 1}/{slides?.length ?? 0}
          </span>
          <Button
            onClick={() => swiperRef.current?.slideNext()}
            hierarchy={"outline"}
            aria-label="Next slide"
            className="custom-next !flex cursor-pointer rounded-full border-0 bg-transparent !px-1 !py-0 transition hover:border-0 hover:bg-transparent active:bg-transparent"
          >
            <span className="text-primary-950 font-[var(--font-medium)]">
              次へ
            </span>
            <SliderNext />
          </Button>
        </div>
      )}
    </>
  );
};

export { Gallery };
